import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "../_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Truck, Loader2, CheckCircle2, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";

const unitLabel = (u) => (u === "m3" ? "m³" : u === "load" ? "loads" : "t");

function CompleteDialog({ open, onOpenChange, item, onDone }) {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF({ loaded_quantity: item?.target_quantity ?? "" }); }, [open, item]);
  const useWb = f.final_gross && f.final_tare;
  const wbNet = useWb ? (Number(f.final_gross) - Number(f.final_tare)).toFixed(2) : null;
  const submit = async () => {
    if (!f.loaded_quantity) return toast.error("Enter the loaded quantity");
    try {
      await api.post(`/sy/loader-queue/${item.id}/complete`, {
        loaded_quantity: Number(f.loaded_quantity),
        final_gross: f.final_gross ? Number(f.final_gross) : null,
        final_tare: f.final_tare ? Number(f.final_tare) : null,
      });
      toast.success("Load complete"); onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="loader-complete-dialog">
        <DialogHeader>
          <DialogTitle className="uppercase font-display text-2xl">{item?.truck_rego} — {item?.product_name}</DialogTitle>
          <DialogDescription>Target {item?.target_quantity}{unitLabel(item?.unit)} from {item?.stockpile_code}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs uppercase font-bold">Actual loaded ({unitLabel(item?.unit)}) *</Label><Input type="number" value={f.loaded_quantity ?? ""} onChange={(e) => setF({ ...f, loaded_quantity: e.target.value })} className="text-lg" data-testid="loaded-qty" /></div>
          <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold">Optional outbound weighbridge</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[10px] uppercase font-bold">Gross (t)</Label><Input type="number" value={f.final_gross || ""} onChange={(e) => setF({ ...f, final_gross: e.target.value })} data-testid="final-gross" /></div>
            <div><Label className="text-[10px] uppercase font-bold">Tare (t)</Label><Input type="number" value={f.final_tare || ""} onChange={(e) => setF({ ...f, final_tare: e.target.value })} data-testid="final-tare" /></div>
          </div>
          {wbNet && <div className="text-sm text-emerald-600 font-bold">Weighbridge net: {wbNet}t (used as final commercial quantity)</div>}
        </div>
        <DialogFooter><Button onClick={submit} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 uppercase text-xs font-bold tracking-wider" data-testid="loader-complete-submit">Complete loading</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SupplyYardLoaderQueue() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("all");
  const [completeItem, setCompleteItem] = useState(null);
  const load = useCallback(() => { api.get("/sy/loader-queue").then((r) => setRows(r.data.queue)).catch(() => {}); }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  const start = async (id) => { try { await api.post(`/sy/loader-queue/${id}/start`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const mins = (iso) => { const t = Date.parse(iso); return isNaN(t) ? null : Math.max(0, Math.round((Date.now() - t) / 60000)); };
  const waiting = rows.filter((r) => r.status !== "LOADING");
  const loading = rows.filter((r) => r.status === "LOADING");
  const shown = tab === "waiting" ? waiting : tab === "loading" ? loading : rows;
  return (
    <div className="px-4 py-4 md:p-6 lg:p-10 md:max-w-[1400px] md:mx-auto pb-24 md:pb-10" data-testid="sy-loader-queue-page">
      <PageHeader tag="supply yard / loader queue" title="Loader Queue" subtitle={`${waiting.length} waiting · ${loading.length} loading`} />
      <div className="flex gap-1.5 mb-4">{[["all", `All (${rows.length})`], ["waiting", `Waiting (${waiting.length})`], ["loading", `Loading (${loading.length})`]].map(([t, l]) => (
        <button key={t} onClick={() => setTab(t)} className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-wider font-bold border ${tab === t ? "bg-[#FF5F15] border-[#FF5F15] text-white" : "border-zinc-300 dark:border-white/15 text-zinc-600 dark:text-zinc-300"}`} data-testid={`loader-tab-${t}`}>{l}</button>
      ))}</div>
      {shown.length === 0 ? (
        <EmptyState icon={Truck} title="Queue is clear" description="No trucks are waiting to be loaded right now." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{shown.map((q) => {
          const loadingCard = q.status === "LOADING";
          const wait = mins(q.queued_at);
          return (
            <div key={q.id} className={`hard-card p-5 ${loadingCard ? "ring-2 ring-[#FF5F15]" : ""}`} data-testid={`queue-card-${q.truck_rego}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="w-9 h-9 rounded-full bg-[#FF5F15]/15 text-[#FF5F15] font-display font-extrabold flex items-center justify-center">{q.position}</span>
                {loadingCard ? <span className="text-[10px] uppercase font-bold text-[#FF5F15] flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading</span>
                  : <span className="text-[11px] text-amber-600 font-bold flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {wait != null ? `${wait} min` : "Waiting"}</span>}
              </div>
              <div className="text-xl font-display font-extrabold">{q.truck_rego || "—"}</div>
              <div className="text-sm text-zinc-500">{q.customer_name}</div>
              <div className="mt-3 rounded-lg bg-zinc-100 dark:bg-white/5 p-3">
                <div className="font-bold">{q.product_name}</div>
                <div className="text-2xl font-display font-extrabold text-[#FF5F15]">{q.target_quantity}<span className="text-sm text-zinc-500"> {unitLabel(q.unit)}</span></div>
                <div className="text-xs text-zinc-500 flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" /> {q.stockpile_code}</div>
              </div>
              {loadingCard ? (
                <Button onClick={() => setCompleteItem(q)} className="w-full mt-3 rounded-sm bg-emerald-600 hover:bg-emerald-700 uppercase text-xs font-bold tracking-wider" data-testid={`queue-complete-${q.truck_rego}`}><CheckCircle2 className="w-4 h-4 mr-1.5" /> Complete loading</Button>
              ) : (
                <Button onClick={() => start(q.id)} className="w-full mt-3 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider" data-testid={`queue-start-${q.truck_rego}`}>Start loading</Button>
              )}
            </div>
          );
        })}</div>
      )}
      <CompleteDialog open={!!completeItem} onOpenChange={(v) => !v && setCompleteItem(null)} item={completeItem} onDone={load} />
    </div>
  );
}
